import "react-responsive-carousel/lib/styles/carousel.min.css";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Control } from "react-hook-form";

import APAccordion from "../../components/APAccordion";
import APDetailRow from "../../components/APDetailRow";
import CatchEButton from "../../components/buttons/CatchEButton";
import VehicleImageCarousel from "../../components/carousels/VehicleImageCarousel";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import TermsButton from "../../components/TermsButton";
import VehicleOptionsList from "../../dossier/vehicleDetail/components/VehicleOptionsList";
import VehicleStandardFeatures from "../../dossier/vehicleDetail/components/VehicleStandardFeatures";
import { PersonaliseQuoteInputs } from "../../helpers/quotes";
import {
  PersonalisedQuoteVehicleInfo,
  QuoteFormatted,
} from "../../types/Quote";
import { RedbookVehiclePhoto, VehicleOptions } from "../../types/Vehicles";
import VehicleOptionTabs from "./VehicleOptionTabs";

interface QuotePersonaliserEventHandlers {
  handleEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
}

interface QuotePersonaliserFlags {
  isSubmitting: boolean;
  hasPricingRequests: boolean;
}

interface QuotePersonaliserProps {
  quote: QuoteFormatted;
  quoteImages: RedbookVehiclePhoto[];
  quoteVehicleInfo: PersonalisedQuoteVehicleInfo;
  vehicleOptions: VehicleOptions;
  control: Control<PersonaliseQuoteInputs>;
  handlers: QuotePersonaliserEventHandlers;
  flags: QuotePersonaliserFlags;
}
const QuotePersonaliser = ({
  quote,
  quoteImages,
  quoteVehicleInfo,
  vehicleOptions,
  control,
  handlers,
  flags,
}: QuotePersonaliserProps) => {
  return (
    <Box>
      <Stack component="form" spacing={2} onSubmit={handlers.handleSubmit}>
        <Box>
          <APAccordion
            title={`${quote.vehicle.year} ${quote.vehicle.make} ${
              quote.vehicle.model
            } ${quote.vehicle.description ?? ""}`}
            hideExpand={quoteImages.length === 0}
            smallHeader={true}
          >
            {quoteImages.length > 0 && (
              <Box pt={2}>
                <VehicleImageCarousel images={quoteImages} />
              </Box>
            )}
          </APAccordion>
        </Box>

        <Box>
          <APAccordion
            title={"Vehicle Details"}
            leftIcon={<EditOutlinedIcon />}
            handleLeftIconClick={handlers.handleEdit}
          >
            <APDetailRow
              label="Base Price"
              value={quote.listPrice.formatted_exc_cents}
            />
            <APDetailRow label="Series" value={quoteVehicleInfo.series} />
            <APDetailRow label="Body" value={quoteVehicleInfo.body} />
            <APDetailRow
              label="Seat Capacity"
              value={`${quoteVehicleInfo.seats}`}
            />
            <APDetailRow
              label="Transmission"
              value={quoteVehicleInfo.transmission}
            />
            <APDetailRow label="Drive" value={quoteVehicleInfo.driveType} />
            <APDetailRow label="Fuel Type" value={quoteVehicleInfo.fuelType} />
            <APDetailRow label="Release Date" value={quote.vehicle.year} />
            <APDetailRow
              label="CO2 Emissions"
              value={`${quoteVehicleInfo.co2Emissions}`}
            />
            <APDetailRow
              label="ANCAP Rating"
              value={`${quoteVehicleInfo.ancapRating} star`}
            />
          </APAccordion>
        </Box>

        <Box>
          <VehicleOptionTabs
            quoteVehicleInfo={quoteVehicleInfo}
            savingsPercentage={quote.savings.raw}
            term={quote.term.raw}
            vehicleOptions={vehicleOptions}
            control={control}
            flags={{
              ...flags,
              isQuoteLocked: quote.isLocked,
              isVehicleConfigurationLocked: quote.isVehicleConfigurationLocked,
            }}
          />
        </Box>

        <Box>
          <VehicleOptionsList
            title="Manufacturer Options"
            options={Object.values(vehicleOptions.options.manufacturer)}
            defaultExpanded={true}
            quote={quote}
            control={control}
            disableCheckboxes={
              flags.hasPricingRequests ||
              quote.isLocked ||
              quote.isVehicleConfigurationLocked
            }
          />
          <TermsButton termsId="vehicleOption" />
        </Box>

        <Box>
          <VehicleStandardFeatures
            features={vehicleOptions.standardEquipment}
            filterable={true}
          />
        </Box>

        <Box
          sx={{
            pt: 2,
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <Box sx={{ mr: 2 }}>
            <RestrictedAppScope scope="internal">
              <CatchEButton url={quote.catchEQuoteUrl} />
            </RestrictedAppScope>
          </Box>
          <Box>
            <Button
              size="large"
              color="primary"
              variant="contained"
              disableElevation={true}
              startIcon={<EditOutlinedIcon />}
              sx={{ mr: 2 }}
              onClick={handlers.handleEdit}
            >
              Edit
            </Button>
          </Box>
          <Box>
            <Button
              size="large"
              color="secondary"
              variant="contained"
              type="submit"
              disableElevation={true}
              startIcon={<PlayArrowOutlinedIcon />}
            >
              View Quote
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default QuotePersonaliser;
